import * as i2 from '@acorex/core/platform';
import * as i6 from '@angular/cdk/portal';
import { TemplatePortal, ComponentPortal, PortalModule } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { TemplateRef, Component, ChangeDetectionStrategy, ViewEncapsulation, Inject, HostListener, inject, Injectable, NgModule } from '@angular/core';
import { MXBaseComponent, AXClosbaleComponent, AXComponentClosedPromise, AXCommonModule } from '@acorex/components/common';
import * as i1 from '@acorex/components/loading';
import * as i3 from '@angular/cdk/dialog';
import { DIALOG_DATA, Dialog, DialogModule } from '@angular/cdk/dialog';
import * as i4 from '@angular/cdk/drag-drop';
import { DragDropModule } from '@angular/cdk/drag-drop';
import * as i5 from '@angular/cdk/a11y';
import { A11yModule } from '@angular/cdk/a11y';
import * as i7 from '@acorex/components/decorators';
import { AXDecoratorModule } from '@acorex/components/decorators';
import * as i8 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i9 from '@acorex/core/translation';
import { AXTranslationModule } from '@acorex/core/translation';

/**
 * The Button is a component which detects user interaction and triggers a corresponding event
 *
 * @category Components
 */
class AXPopupComponent extends MXBaseComponent {
    /**
     *  @ignore
     */
    constructor(_zone, _viewContainerRef, loadingService, _platform, data, dialogRef) {
        super();
        this._zone = _zone;
        this._viewContainerRef = _viewContainerRef;
        this.loadingService = loadingService;
        this._platform = _platform;
        this.data = data;
        this.dialogRef = dialogRef;
        this.isLoading = true;
    }
    ngOnInit() {
        super.ngOnInit();
        if (this._platform.is('Mobile')) {
            this.data.draggable = false;
        }
        this._loadingId = this.loadingService.show({
            location: this.getHostElement(),
        });
        //
        if (this.data.content instanceof TemplateRef) {
            this._selectedPortal = new TemplatePortal(this.data.content, this._viewContainerRef, {
                $implicit: this.data,
                ref: this,
            });
            this.cdr.markForCheck();
        }
        else if (typeof this.data.content === 'function') {
            this._selectedPortal = new ComponentPortal(this.data.content);
            this.cdr.markForCheck();
        }
    }
    _handleAttched(ref) {
        ref = ref;
        if (ref.instance) {
            this._componentRef = ref.instance;
            Object.assign(this._componentRef, this.data.data);
            Object.assign(this._componentRef, { _isPopup: true });
            Object.assign(this._componentRef, {
                setTitle: (title) => {
                    this.data.title = title;
                },
            });
            if (ref.instance.onClosed) {
                ref.instance.onClosed.subscribe((e) => {
                    this.close(e.data);
                });
            }
        }
        this._zone.runOutsideAngular(() => {
            setTimeout(() => {
                const main = this.getHostElement().querySelector('.ax-popup-main-container');
                const popHeader = this.getHostElement().querySelector('.ax-popup-header-container');
                const popFooter = this.getHostElement().querySelector('.ax-popup-footer-container');
                const footer = main.querySelector('ax-footer');
                const header = main.querySelector('ax-header');
                if (footer) {
                    popFooter?.append(footer);
                }
                if (header) {
                    popHeader.innerHTML = '';
                    popHeader.append(header);
                }
                this.focus();
            });
        });
        this.loadingService.hide(this._loadingId);
        this.cdr.markForCheck();
    }
    onKeydownHandler(event) {
        const focusedOrHasFocused = this.getHostElement().matches(':focus-within');
        if (this.data.closeButton && focusedOrHasFocused) {
            this.close();
        }
    }
    _handleCloseClick() {
        this.close();
    }
    ngOnDestroy() {
        this.loadingService.hide(this._loadingId);
    }
    focus() {
        setTimeout(() => this.getHostElement().querySelector('.ax-popup')?.focus());
    }
    close(data) {
        const closeFn = () => {
            this.dialogRef.close({
                component: this._componentRef,
                htmlElement: this.getHostElement(),
                data: data,
            });
        };
        if (typeof this._componentRef.onClosing === 'function') {
            const ee = {
                cancel: false,
                data,
            };
            const closingResult = this._componentRef.onClosing(ee);
            if (closingResult instanceof Promise) {
                closingResult.then(() => {
                    if (!ee.cancel) {
                        closeFn();
                    }
                });
            }
            else {
                if (!ee.cancel) {
                    closeFn();
                }
            }
        }
        else {
            closeFn();
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopupComponent, deps: [{ token: i0.NgZone }, { token: i0.ViewContainerRef }, { token: i1.AXLoadingService }, { token: i2.AXPlatform }, { token: DIALOG_DATA }, { token: i3.DialogRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.0.2", type: AXPopupComponent, selector: "ax-popup", host: { listeners: { "keydown.escape": "onKeydownHandler($event)" } }, providers: [{ provide: AXClosbaleComponent, useExisting: AXPopupComponent }], usesInheritance: true, ngImport: i0, template: "<div class=\"ax-popup-wrapper \" aria-modal=\"true\" [cdkTrapFocus]=\"true\">\n  <div class=\"ax-popup  ax-popup-{{ data.size }}\" tabindex=\"0\" cdkDrag [cdkDragDisabled]=\"!data.draggable\">\n    <div cdkDragHandle class=\"ax-popup-header-container\">\n      @if(data.header){\n      <div class=\"ax-popup-header\">\n        <span class=\"ax-popup-title\">{{ data.title | translate | async }}</span>\n        @if(data.closeButton){\n        <ax-close-button tabindex=\"1\"></ax-close-button>\n        }\n      </div>\n      }\n    </div>\n    <div class=\"ax-popup-main-container ax-loading-container\">\n      <div class=\"ax-popup-body-container\">\n        <ng-template [cdkPortalOutlet]=\"_selectedPortal\" (attached)=\"_handleAttched($event)\"></ng-template>\n      </div>\n      <div class=\"ax-popup-footer-container\"></div>\n    </div>\n  </div>\n</div>", styles: [".ax-popup{max-height:calc(var(--ax-overlay-full-width) * var(--ax-vh));min-height:fit-content;margin-left:auto;margin-right:auto;display:flex;width:auto;flex-direction:column;overflow:hidden;border-radius:var(--ax-rounded-border-default);border-width:1px;border-color:rgba(var(--ax-color-border-default));background-color:rgba(var(--ax-color-surface));padding:0;--ax-shadow: 0 10px 15px -3px rgb(0 0 0 / .1), 0 4px 6px -4px rgb(0 0 0 / .1);--ax-shadow-colored: 0 10px 15px -3px var(--ax-shadow-color), 0 4px 6px -4px var(--ax-shadow-color);box-shadow:var(--ax-ring-offset-shadow, 0 0 rgba(0, 0, 0, 0)),var(--ax-ring-shadow, 0 0 rgba(0, 0, 0, 0)),var(--ax-shadow);outline:2px solid transparent;outline-offset:2px}.ax-popup .ax-popup-header{display:flex;align-items:center;justify-content:space-between;border-bottom-width:1px;border-color:rgba(var(--ax-color-border-default));padding:.75rem}.ax-popup .ax-popup-header .ax-popup-title{font-size:1rem;line-height:1;font-weight:500}@media (min-width: 768px){.ax-popup .ax-popup-header .ax-popup-title{font-size:1.125rem;line-height:1.75rem}}.ax-popup ax-footer{box-shadow:0 2px 10px #0000004d;display:flex;align-items:center;justify-content:space-between;border-top-width:1px;border-color:rgba(var(--ax-color-border-default));padding:.75rem}.ax-popup:focus{outline:2px solid transparent;outline-offset:2px}.ax-popup:focus-visible{border-color:rgba(var(--ax-color-primary-500))}.ax-popup .ax-popup-main-container{display:flex;flex:1 1 0%;flex-direction:column;overflow:hidden}.ax-popup .ax-popup-main-container .ax-popup-body-container{flex:1 1 0%;overflow:auto}.ax-popup.ax-popup-full{width:100vw;max-height:calc(100 * var(--ax-vh));height:calc(100 * var(--ax-vh));border-radius:0!important}.ax-popup.ax-fit{width:fit-content!important}.ax-popup.ax-fit .ax-popup-body-container>ng-component>div{width:fit-content!important}@media (min-width: 320px) and (max-width: 600px){.ax-popup.ax-popup-sm,.ax-popup.ax-popup-md,.ax-popup.ax-popup-lg{width:93vw!important;height:fit-content;max-height:calc(var(--ax-overlay-full-width) * var(--ax-vh))!important}.ax-popup.ax-popup-sm .ax-popup-body-container>ng-component>div,.ax-popup.ax-popup-md .ax-popup-body-container>ng-component>div,.ax-popup.ax-popup-lg .ax-popup-body-container>ng-component>div{width:100%}}@media (min-width: 768px){.ax-popup.ax-popup-sm{width:55vw!important}.ax-popup.ax-popup-md{width:65vw!important}.ax-popup.ax-popup-lg{width:75vw!important}}@media (min-width: 1280px){.ax-popup.ax-popup-sm{width:30vw!important}.ax-popup.ax-popup-md{width:50vw!important}.ax-popup.ax-popup-lg{width:85vw!important}}@media (min-width: 1536px){.ax-popup.ax-popup-sm{width:25vw!important}.ax-popup.ax-popup-md{width:40vw!important}.ax-popup.ax-popup-lg{width:65vw!important}}\n"], dependencies: [{ kind: "directive", type: i4.CdkDrag, selector: "[cdkDrag]", inputs: ["cdkDragData", "cdkDragLockAxis", "cdkDragRootElement", "cdkDragBoundary", "cdkDragStartDelay", "cdkDragFreeDragPosition", "cdkDragDisabled", "cdkDragConstrainPosition", "cdkDragPreviewClass", "cdkDragPreviewContainer"], outputs: ["cdkDragStarted", "cdkDragReleased", "cdkDragEnded", "cdkDragEntered", "cdkDragExited", "cdkDragDropped", "cdkDragMoved"], exportAs: ["cdkDrag"] }, { kind: "directive", type: i4.CdkDragHandle, selector: "[cdkDragHandle]", inputs: ["cdkDragHandleDisabled"] }, { kind: "directive", type: i5.CdkTrapFocus, selector: "[cdkTrapFocus]", inputs: ["cdkTrapFocus", "cdkTrapFocusAutoCapture"], exportAs: ["cdkTrapFocus"] }, { kind: "directive", type: i6.CdkPortalOutlet, selector: "[cdkPortalOutlet]", inputs: ["cdkPortalOutlet"], outputs: ["attached"], exportAs: ["cdkPortalOutlet"] }, { kind: "component", type: i7.AXDecoratorCloseButtonComponent, selector: "ax-close-button", inputs: ["icon"] }, { kind: "pipe", type: i8.AsyncPipe, name: "async" }, { kind: "pipe", type: i9.AXTranslatorPipe, name: "translate" }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopupComponent, decorators: [{
            type: Component,
            args: [{ selector: 'ax-popup', changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, providers: [{ provide: AXClosbaleComponent, useExisting: AXPopupComponent }], template: "<div class=\"ax-popup-wrapper \" aria-modal=\"true\" [cdkTrapFocus]=\"true\">\n  <div class=\"ax-popup  ax-popup-{{ data.size }}\" tabindex=\"0\" cdkDrag [cdkDragDisabled]=\"!data.draggable\">\n    <div cdkDragHandle class=\"ax-popup-header-container\">\n      @if(data.header){\n      <div class=\"ax-popup-header\">\n        <span class=\"ax-popup-title\">{{ data.title | translate | async }}</span>\n        @if(data.closeButton){\n        <ax-close-button tabindex=\"1\"></ax-close-button>\n        }\n      </div>\n      }\n    </div>\n    <div class=\"ax-popup-main-container ax-loading-container\">\n      <div class=\"ax-popup-body-container\">\n        <ng-template [cdkPortalOutlet]=\"_selectedPortal\" (attached)=\"_handleAttched($event)\"></ng-template>\n      </div>\n      <div class=\"ax-popup-footer-container\"></div>\n    </div>\n  </div>\n</div>", styles: [".ax-popup{max-height:calc(var(--ax-overlay-full-width) * var(--ax-vh));min-height:fit-content;margin-left:auto;margin-right:auto;display:flex;width:auto;flex-direction:column;overflow:hidden;border-radius:var(--ax-rounded-border-default);border-width:1px;border-color:rgba(var(--ax-color-border-default));background-color:rgba(var(--ax-color-surface));padding:0;--ax-shadow: 0 10px 15px -3px rgb(0 0 0 / .1), 0 4px 6px -4px rgb(0 0 0 / .1);--ax-shadow-colored: 0 10px 15px -3px var(--ax-shadow-color), 0 4px 6px -4px var(--ax-shadow-color);box-shadow:var(--ax-ring-offset-shadow, 0 0 rgba(0, 0, 0, 0)),var(--ax-ring-shadow, 0 0 rgba(0, 0, 0, 0)),var(--ax-shadow);outline:2px solid transparent;outline-offset:2px}.ax-popup .ax-popup-header{display:flex;align-items:center;justify-content:space-between;border-bottom-width:1px;border-color:rgba(var(--ax-color-border-default));padding:.75rem}.ax-popup .ax-popup-header .ax-popup-title{font-size:1rem;line-height:1;font-weight:500}@media (min-width: 768px){.ax-popup .ax-popup-header .ax-popup-title{font-size:1.125rem;line-height:1.75rem}}.ax-popup ax-footer{box-shadow:0 2px 10px #0000004d;display:flex;align-items:center;justify-content:space-between;border-top-width:1px;border-color:rgba(var(--ax-color-border-default));padding:.75rem}.ax-popup:focus{outline:2px solid transparent;outline-offset:2px}.ax-popup:focus-visible{border-color:rgba(var(--ax-color-primary-500))}.ax-popup .ax-popup-main-container{display:flex;flex:1 1 0%;flex-direction:column;overflow:hidden}.ax-popup .ax-popup-main-container .ax-popup-body-container{flex:1 1 0%;overflow:auto}.ax-popup.ax-popup-full{width:100vw;max-height:calc(100 * var(--ax-vh));height:calc(100 * var(--ax-vh));border-radius:0!important}.ax-popup.ax-fit{width:fit-content!important}.ax-popup.ax-fit .ax-popup-body-container>ng-component>div{width:fit-content!important}@media (min-width: 320px) and (max-width: 600px){.ax-popup.ax-popup-sm,.ax-popup.ax-popup-md,.ax-popup.ax-popup-lg{width:93vw!important;height:fit-content;max-height:calc(var(--ax-overlay-full-width) * var(--ax-vh))!important}.ax-popup.ax-popup-sm .ax-popup-body-container>ng-component>div,.ax-popup.ax-popup-md .ax-popup-body-container>ng-component>div,.ax-popup.ax-popup-lg .ax-popup-body-container>ng-component>div{width:100%}}@media (min-width: 768px){.ax-popup.ax-popup-sm{width:55vw!important}.ax-popup.ax-popup-md{width:65vw!important}.ax-popup.ax-popup-lg{width:75vw!important}}@media (min-width: 1280px){.ax-popup.ax-popup-sm{width:30vw!important}.ax-popup.ax-popup-md{width:50vw!important}.ax-popup.ax-popup-lg{width:85vw!important}}@media (min-width: 1536px){.ax-popup.ax-popup-sm{width:25vw!important}.ax-popup.ax-popup-md{width:40vw!important}.ax-popup.ax-popup-lg{width:65vw!important}}\n"] }]
        }], ctorParameters: () => [{ type: i0.NgZone }, { type: i0.ViewContainerRef }, { type: i1.AXLoadingService }, { type: i2.AXPlatform }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [DIALOG_DATA]
                }] }, { type: i3.DialogRef }], propDecorators: { onKeydownHandler: [{
                type: HostListener,
                args: ['keydown.escape', ['$event']]
            }] } });

/**
 * This is a service which you can create popup with it
 *
 * @category Components
 */
class AXPopupService {
    constructor() {
        this.dialog = inject(Dialog);
    }
    /**
     *  @ignore
     */
    open(arg1, arg2) {
        let config = {
            closeButton: true,
            header: true,
            size: 'md',
            maximizable: false,
            draggable: true,
            hasBackdrop: true,
            closeOnBackdropClick: false,
            data: {},
        };
        if (typeof arg2 === 'string') {
            config.title = arg2;
        }
        else {
            config = Object.assign(config, arg2);
        }
        const dialogRef = this.dialog.open(AXPopupComponent, {
            data: Object.assign(config, { content: arg1 }),
            autoFocus: true,
            restoreFocus: true,
            closeOnNavigation: true,
            closeOnDestroy: true,
            hasBackdrop: config.hasBackdrop,
            backdropClass: config.backdropClass,
            disableClose: config.closeOnBackdropClick ? false : true,
            viewContainerRef: config.viewContainerRef,
            providers: config.providers,
        });
        const promise = new AXComponentClosedPromise((resolve) => {
            dialogRef.closed.subscribe((d) => {
                if (resolve) {
                    resolve({
                        sender: d.component,
                        data: d.data,
                    });
                }
            });
        });
        return promise;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopupService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopupService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopupService, decorators: [{
            type: Injectable
        }] });

const COMPONENT = [AXPopupComponent];
const MODULES = [
    CommonModule,
    AXCommonModule,
    DragDropModule,
    A11yModule,
    PortalModule,
    AXDecoratorModule,
    DialogModule,
    AXTranslationModule,
];
class AXPopupModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.0.2", ngImport: i0, type: AXPopupModule, declarations: [AXPopupComponent], imports: [CommonModule,
            AXCommonModule,
            DragDropModule,
            A11yModule,
            PortalModule,
            AXDecoratorModule,
            DialogModule,
            AXTranslationModule], exports: [AXPopupComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopupModule, providers: [AXPopupService], imports: [MODULES] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.0.2", ngImport: i0, type: AXPopupModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [...COMPONENT],
                    imports: [...MODULES],
                    exports: [...COMPONENT],
                    providers: [AXPopupService],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AXPopupComponent, AXPopupModule, AXPopupService };
